import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import EzCalendar from "../EzCalendar/EzCalendar.es.js";
import { InsetIcon, CalendarIcon } from "../Icons/index.es.js";
import { useMenuTriggerState, useMenuTrigger } from "../Overlays.es.js";
import stitches from "../theme.config.es.js";
import EzTextInput from "./EzTextInput.es.js";
import { TextInputWrapper } from "./Picker.styles.es.js";
import useUpdateEffect from "../../utils/hooks/useUpdateEffect.es.js";
import EzIcon from "../EzIcon/EzIcon.es.js";
import EzPopover from "../EzPopover/EzPopover.es.js";
import FocusScope from "../utils/FocusScope/FocusScope.es.js";
import { clsx } from "../../utils/mergeProps.es.js";
const layout = stitches.css({
  position: "relative",
  width: "200px",
  input: { paddingLeft: "2.5em" }
});
const popper = stitches.css({
  zIndex: "$date-input-z"
  // this is hard-coded in theme.config.ts for now, but we should really pull it in from mui when we convert date inputs to mui
});
const calendarPopup = stitches.css({
  "@baseToMedium": {
    maxWidth: "250px",
    width: "auto"
  },
  position: "relative",
  padding: "$150",
  fontFamily: "$defaultFont",
  fontSize: "$100",
  width: "320px",
  marginTop: "$100",
  borderRadius: 8,
  border: "1px solid $border",
  backgroundColor: "white",
  boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.12)"
});
const arrow = stitches.css({
  "&:after, &:before": {
    content: "' '",
    position: "absolute",
    left: "19px",
    bottom: "100%",
    height: "0",
    width: "0"
  },
  "&:after": {
    marginLeft: "-5px",
    border: "5px solid transparent",
    borderBottomColor: "white"
  },
  "&:before": {
    marginLeft: "-7px",
    border: "7px solid transparent",
    borderBottomColor: "$border"
  }
});
const iconWrapperStyle = stitches.css({
  alignItems: "center",
  display: "flex",
  fontSize: "13px"
});
const EzDateInput = ({
  id,
  name,
  disabled,
  onChange,
  "aria-labelledby": ariaLabelledBy,
  ...props
}) => {
  const { placeholder = "Select date" } = props;
  const [value, setValue] = useState(props.value);
  const [validDate, setValidDate] = useState(dayjs(value).isValid() ? value : null);
  const menuState = useMenuTriggerState();
  const { close, isOpen } = menuState;
  const calendarRef = useRef();
  const onChangeRef = useRef(onChange);
  const triggerRef = useRef();
  useUpdateEffect(() => {
    if (dayjs(value).isValid()) setValidDate(value);
  }, [value]);
  useUpdateEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useUpdateEffect(() => {
    onChangeRef.current(validDate);
  }, [validDate]);
  useEffect(() => {
    setValue(props.value);
  }, [props.value]);
  const { menuTriggerProps, menuProps } = useMenuTrigger(menuState);
  const comboboxInput = {
    ...menuTriggerProps,
    ref: triggerRef,
    id,
    name,
    value,
    "aria-haspopup": "dialog",
    "aria-labelledby": ariaLabelledBy,
    onChange: (e) => setValue(e.target.value),
    disabled,
    placeholder,
    error: props.error,
    touched: props.touched,
    onFocus: props.onFocus,
    onFocusCapture: props.onFocusCapture
  };
  const { minDate, maxDate, filterDate } = props;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      TextInputWrapper,
      {
        className: layout({ className: props.className }),
        disabled,
        onKeyDown: (e) => {
          if (e.key !== "Tab" || !isOpen) return;
          e.preventDefault();
          calendarRef.current.focus();
        },
        children: [
          /* @__PURE__ */ jsx(InsetIcon, { insetY0: true, left0: true, pl3: true, children: /* @__PURE__ */ jsx(CalendarIcon, {}) }),
          /* @__PURE__ */ jsx(EzTextInput, { ...comboboxInput, type: "text" }),
          /* @__PURE__ */ jsx(InsetIcon, { insetY0: true, right0: true, pr2: true, children: /* @__PURE__ */ jsx("div", { className: iconWrapperStyle(), children: /* @__PURE__ */ jsx(EzIcon, { icon: isOpen ? faChevronUp : faChevronDown, size: "inherit" }) }) })
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsx(
      EzPopover,
      {
        targetRef: triggerRef,
        placement: "bottom-start",
        shouldCloseOnBlur: true,
        onClose: close,
        className: popper(),
        classNameSuffix: props.classNameSuffix,
        children: /* @__PURE__ */ jsx(FocusScope, { contain: true, restoreFocus: true, children: /* @__PURE__ */ jsx(
          "div",
          {
            ...menuProps,
            className: clsx(calendarPopup(), arrow()),
            role: "dialog",
            "aria-labelledby": id,
            onKeyDown: (e) => {
              if (e.key !== "Escape") return;
              e.stopPropagation();
              close();
            },
            children: /* @__PURE__ */ jsx(
              EzCalendar,
              {
                ...{ minDate, maxDate, filterDate },
                ref: calendarRef,
                value: validDate,
                onChange: (date) => {
                  setValue(date);
                  close();
                }
              }
            )
          }
        ) })
      }
    )
  ] });
};
export {
  EzDateInput as default
};
//# sourceMappingURL=EzDateInput.es.js.map
